import React from "react"

/**
 * Kitchen section SVG illustrations for the V5 CPQ configurator.
 * Each SVG is ~200px wide, monochrome line art with #3b82f6 accent
 * highlighting the relevant section.
 *
 * Optional props: topSwatchUrl, bottomSwatchUrl — when provided,
 * the cabinet faces render with the swatch texture image as a preview.
 */

const ACCENT = "#3b82f6"
const ACCENT_LIGHT = "#dbeafe"
const STROKE = "#94a3b8"
const STROKE_LIGHT = "#cbd5e1"
const FILL_BG = "#f8fafc"

// ─── Helper: swatch-filled rect or plain rect ───────────────────
const SwatchRect = ({ id, x, y, w, h, rx, swatchUrl, fillActive, fillInactive, strokeColor, strokeW = 1.5, isActive }) => {
  const fill = isActive ? fillActive : fillInactive
  if (!swatchUrl) {
    return (
      <rect x={x} y={y} width={w} height={h} rx={rx}
        fill={fill} stroke={strokeColor} strokeWidth={strokeW} />
    )
  }
  return (
    <>
      <defs>
        <clipPath id={id}>
          <rect x={x} y={y} width={w} height={h} rx={rx} />
        </clipPath>
      </defs>
      <g clipPath={`url(#${id})`}>
        <image href={swatchUrl} x={x} y={y} width={w} height={h}
          preserveAspectRatio="xMidYMid slice" />
        {/* Light overlay to keep it subtle */}
        <rect x={x} y={y} width={w} height={h}
          fill={isActive ? "rgba(255,255,255,0.15)" : "rgba(255,255,255,0.55)"} />
      </g>
      <rect x={x} y={y} width={w} height={h} rx={rx}
        fill="none" stroke={strokeColor} strokeWidth={strokeW} />
    </>
  )
}

/**
 * Wall Cabinets (Kremasta / TOP section)
 * Side-view: upper cabinets highlighted in blue, base cabinets in grey outline
 */
export const WallCabinetsSvg = ({ width = 200, height = 140, topSwatchUrl, bottomSwatchUrl, countertopColor }) => {
  const hasTop = Boolean(topSwatchUrl)
  const hasBottom = Boolean(bottomSwatchUrl)

  return (
    <svg width={width} height={height} viewBox="0 0 200 140" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Back wall */}
      <rect x="20" y="8" width="160" height="126" rx="2" fill={FILL_BG} stroke={STROKE_LIGHT} strokeWidth="1" />

      {/* === WALL CABINETS (highlighted / swatch-filled) === */}
      <SwatchRect id="wc-l" x={30} y={16} w={42} h={40} rx={3}
        swatchUrl={topSwatchUrl} isActive
        fillActive={ACCENT_LIGHT} fillInactive={ACCENT_LIGHT}
        strokeColor={ACCENT} strokeW={2} />
      <SwatchRect id="wc-c" x={78} y={16} w={44} h={40} rx={3}
        swatchUrl={topSwatchUrl} isActive
        fillActive={ACCENT_LIGHT} fillInactive={ACCENT_LIGHT}
        strokeColor={ACCENT} strokeW={2} />
      <SwatchRect id="wc-r" x={128} y={16} w={42} h={40} rx={3}
        swatchUrl={topSwatchUrl} isActive
        fillActive={ACCENT_LIGHT} fillInactive={ACCENT_LIGHT}
        strokeColor={ACCENT} strokeW={2} />

      {/* Wall cabinet details (door lines + handles) */}
      {!hasTop && (
        <>
          <line x1="51" y1="16" x2="51" y2="56" stroke={ACCENT} strokeWidth="1.5" strokeDasharray="2 2" />
          <circle cx="45" cy="36" r="2" fill={ACCENT} />
          <circle cx="57" cy="36" r="2" fill={ACCENT} />
          <rect x="84" y="22" width="14" height="28" rx="1" fill="none" stroke={ACCENT} strokeWidth="1" opacity="0.5" />
          <rect x="102" y="22" width="14" height="28" rx="1" fill="none" stroke={ACCENT} strokeWidth="1" opacity="0.5" />
          <circle cx="96" cy="36" r="2" fill={ACCENT} />
          <line x1="149" y1="16" x2="149" y2="56" stroke={ACCENT} strokeWidth="1.5" strokeDasharray="2 2" />
          <circle cx="143" cy="36" r="2" fill={ACCENT} />
          <circle cx="155" cy="36" r="2" fill={ACCENT} />
        </>
      )}
      {hasTop && (
        <>
          <line x1="51" y1="16" x2="51" y2="56" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5" />
          <circle cx="45" cy="36" r="2" fill="rgba(255,255,255,0.6)" />
          <circle cx="57" cy="36" r="2" fill="rgba(255,255,255,0.6)" />
          <line x1="100" y1="16" x2="100" y2="56" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5" />
          <circle cx="94" cy="36" r="2" fill="rgba(255,255,255,0.6)" />
          <circle cx="106" cy="36" r="2" fill="rgba(255,255,255,0.6)" />
          <line x1="149" y1="16" x2="149" y2="56" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5" />
          <circle cx="143" cy="36" r="2" fill="rgba(255,255,255,0.6)" />
          <circle cx="155" cy="36" r="2" fill="rgba(255,255,255,0.6)" />
        </>
      )}

      {/* === COUNTERTOP === */}
      <rect x="28" y="72" width="144" height="6" rx="1" fill={countertopColor || "#e2e8f0"} stroke={countertopColor ? "rgba(0,0,0,0.15)" : STROKE_LIGHT} strokeWidth="1" />

      {/* === BASE CABINETS === */}
      <SwatchRect id="wc-bl" x={30} y={80} w={42} h={44} rx={3}
        swatchUrl={bottomSwatchUrl} isActive={false}
        fillActive="none" fillInactive="none"
        strokeColor={hasBottom ? STROKE : STROKE_LIGHT} strokeW={1.5} />
      <SwatchRect id="wc-bc" x={78} y={80} w={44} h={44} rx={3}
        swatchUrl={bottomSwatchUrl} isActive={false}
        fillActive="none" fillInactive="none"
        strokeColor={hasBottom ? STROKE : STROKE_LIGHT} strokeW={1.5} />
      <SwatchRect id="wc-br" x={128} y={80} w={42} h={44} rx={3}
        swatchUrl={bottomSwatchUrl} isActive={false}
        fillActive="none" fillInactive="none"
        strokeColor={hasBottom ? STROKE : STROKE_LIGHT} strokeW={1.5} />

      {/* Base handles (subtle) */}
      {hasBottom ? (
        <>
          <line x1="46" y1="96" x2="58" y2="96" stroke="rgba(255,255,255,0.6)" strokeWidth="2" strokeLinecap="round" />
          <line x1="94" y1="96" x2="108" y2="96" stroke="rgba(255,255,255,0.6)" strokeWidth="2" strokeLinecap="round" />
          <line x1="144" y1="96" x2="156" y2="96" stroke="rgba(255,255,255,0.6)" strokeWidth="2" strokeLinecap="round" />
        </>
      ) : (
        <>
          <line x1="46" y1="96" x2="58" y2="96" stroke={STROKE_LIGHT} strokeWidth="2" strokeLinecap="round" />
          <line x1="94" y1="96" x2="108" y2="96" stroke={STROKE_LIGHT} strokeWidth="2" strokeLinecap="round" />
          <line x1="144" y1="96" x2="156" y2="96" stroke={STROKE_LIGHT} strokeWidth="2" strokeLinecap="round" />
        </>
      )}

      {/* Floor line */}
      <line x1="20" y1="130" x2="180" y2="130" stroke={STROKE_LIGHT} strokeWidth="1" />
    </svg>
  )
}

/**
 * Base Cabinets (Vaseos / BOTTOM section)
 * Side-view: base cabinets highlighted in blue, wall cabinets in grey outline
 */
export const BaseCabinetsSvg = ({ width = 200, height = 140, topSwatchUrl, bottomSwatchUrl, countertopColor }) => {
  const hasTop = Boolean(topSwatchUrl)
  const hasBottom = Boolean(bottomSwatchUrl)

  return (
    <svg width={width} height={height} viewBox="0 0 200 140" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Back wall */}
      <rect x="20" y="8" width="160" height="126" rx="2" fill={FILL_BG} stroke={STROKE_LIGHT} strokeWidth="1" />

      {/* === WALL CABINETS === */}
      <SwatchRect id="bc-tl" x={30} y={16} w={42} h={40} rx={3}
        swatchUrl={topSwatchUrl} isActive={false}
        fillActive="none" fillInactive="none"
        strokeColor={hasTop ? STROKE : STROKE_LIGHT} strokeW={1.5} />
      <SwatchRect id="bc-tc" x={78} y={16} w={44} h={40} rx={3}
        swatchUrl={topSwatchUrl} isActive={false}
        fillActive="none" fillInactive="none"
        strokeColor={hasTop ? STROKE : STROKE_LIGHT} strokeW={1.5} />
      <SwatchRect id="bc-tr" x={128} y={16} w={42} h={40} rx={3}
        swatchUrl={topSwatchUrl} isActive={false}
        fillActive="none" fillInactive="none"
        strokeColor={hasTop ? STROKE : STROKE_LIGHT} strokeW={1.5} />

      {/* Wall handles */}
      {hasTop && (
        <>
          <line x1="51" y1="16" x2="51" y2="56" stroke="rgba(255,255,255,0.5)" strokeWidth="1" />
          <line x1="149" y1="16" x2="149" y2="56" stroke="rgba(255,255,255,0.5)" strokeWidth="1" />
        </>
      )}

      {/* === COUNTERTOP === */}
      <rect x="28" y="72" width="144" height="6" rx="1" fill={countertopColor || "#e2e8f0"} stroke={countertopColor ? "rgba(0,0,0,0.15)" : STROKE_LIGHT} strokeWidth="1" />

      {/* === BASE CABINETS (highlighted / swatch-filled) === */}
      {/* Left base - door cabinet */}
      <SwatchRect id="bc-bl" x={30} y={80} w={42} h={44} rx={3}
        swatchUrl={bottomSwatchUrl} isActive
        fillActive={ACCENT_LIGHT} fillInactive={ACCENT_LIGHT}
        strokeColor={ACCENT} strokeW={2} />
      {/* Center base - sink cabinet */}
      <SwatchRect id="bc-bc" x={78} y={80} w={44} h={44} rx={3}
        swatchUrl={bottomSwatchUrl} isActive
        fillActive={ACCENT_LIGHT} fillInactive={ACCENT_LIGHT}
        strokeColor={ACCENT} strokeW={2} />
      {/* Right base - drawer cabinet */}
      <SwatchRect id="bc-br" x={128} y={80} w={42} h={44} rx={3}
        swatchUrl={bottomSwatchUrl} isActive
        fillActive={ACCENT_LIGHT} fillInactive={ACCENT_LIGHT}
        strokeColor={ACCENT} strokeW={2} />

      {/* Base cabinet details */}
      {!hasBottom ? (
        <>
          {/* Door lines + handles (blue accent) */}
          <line x1="51" y1="80" x2="51" y2="124" stroke={ACCENT} strokeWidth="1.5" strokeDasharray="2 2" />
          <circle cx="45" cy="100" r="2" fill={ACCENT} />
          <circle cx="57" cy="100" r="2" fill={ACCENT} />
          {/* Sink basin */}
          <rect x="86" y="85" width="28" height="8" rx="4" fill="none" stroke={ACCENT} strokeWidth="1" opacity="0.6" />
          <circle cx="100" cy="89" r="2" fill={ACCENT} opacity="0.4" />
          <line x1="100" y1="96" x2="100" y2="124" stroke={ACCENT} strokeWidth="1.5" strokeDasharray="2 2" />
          <circle cx="94" cy="108" r="2" fill={ACCENT} />
          <circle cx="106" cy="108" r="2" fill={ACCENT} />
          {/* Drawer lines */}
          <line x1="132" y1="94" x2="166" y2="94" stroke={ACCENT} strokeWidth="1" />
          <line x1="132" y1="108" x2="166" y2="108" stroke={ACCENT} strokeWidth="1" />
          <line x1="144" y1="87" x2="152" y2="87" stroke={ACCENT} strokeWidth="2" strokeLinecap="round" />
          <line x1="144" y1="101" x2="152" y2="101" stroke={ACCENT} strokeWidth="2" strokeLinecap="round" />
          <line x1="144" y1="115" x2="152" y2="115" stroke={ACCENT} strokeWidth="2" strokeLinecap="round" />
        </>
      ) : (
        <>
          {/* Swatch-mode: white overlays for details */}
          <line x1="51" y1="80" x2="51" y2="124" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5" />
          <circle cx="45" cy="100" r="2" fill="rgba(255,255,255,0.6)" />
          <circle cx="57" cy="100" r="2" fill="rgba(255,255,255,0.6)" />
          <rect x="86" y="85" width="28" height="8" rx="4" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="1" />
          <line x1="100" y1="96" x2="100" y2="124" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5" />
          <circle cx="94" cy="108" r="2" fill="rgba(255,255,255,0.6)" />
          <circle cx="106" cy="108" r="2" fill="rgba(255,255,255,0.6)" />
          <line x1="132" y1="94" x2="166" y2="94" stroke="rgba(255,255,255,0.45)" strokeWidth="1" />
          <line x1="132" y1="108" x2="166" y2="108" stroke="rgba(255,255,255,0.45)" strokeWidth="1" />
          <line x1="144" y1="87" x2="152" y2="87" stroke="rgba(255,255,255,0.6)" strokeWidth="2" strokeLinecap="round" />
          <line x1="144" y1="101" x2="152" y2="101" stroke="rgba(255,255,255,0.6)" strokeWidth="2" strokeLinecap="round" />
          <line x1="144" y1="115" x2="152" y2="115" stroke="rgba(255,255,255,0.6)" strokeWidth="2" strokeLinecap="round" />
        </>
      )}

      {/* Floor line */}
      <line x1="20" y1="130" x2="180" y2="130" stroke={STROKE_LIGHT} strokeWidth="1" />
    </svg>
  )
}

/**
 * Countertop (Pagkos) — also shows swatch previews on wall + base if set
 */
export const CountertopSvg = ({ width = 200, height = 140, topSwatchUrl, bottomSwatchUrl, countertopColor }) => {
  const hasTop = Boolean(topSwatchUrl)
  const hasBottom = Boolean(bottomSwatchUrl)
  const hasCt = Boolean(countertopColor)
  // Use selected color or default accent
  const ctFill = hasCt ? countertopColor : ACCENT_LIGHT
  const ctStroke = hasCt ? "rgba(0,0,0,0.25)" : ACCENT
  const ctDetailColor = hasCt ? "rgba(255,255,255,0.35)" : ACCENT
  const ctShadow = hasCt ? "rgba(0,0,0,0.15)" : ACCENT

  return (
    <svg width={width} height={height} viewBox="0 0 200 140" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Back wall */}
      <rect x="20" y="8" width="160" height="126" rx="2" fill={FILL_BG} stroke={STROKE_LIGHT} strokeWidth="1" />

      {/* === WALL CABINETS === */}
      <SwatchRect id="ct-tl" x={30} y={16} w={42} h={40} rx={3}
        swatchUrl={topSwatchUrl} isActive={false}
        fillActive="none" fillInactive="none"
        strokeColor={hasTop ? STROKE : STROKE_LIGHT} strokeW={1.5} />
      <SwatchRect id="ct-tc" x={78} y={16} w={44} h={40} rx={3}
        swatchUrl={topSwatchUrl} isActive={false}
        fillActive="none" fillInactive="none"
        strokeColor={hasTop ? STROKE : STROKE_LIGHT} strokeW={1.5} />
      <SwatchRect id="ct-tr" x={128} y={16} w={42} h={40} rx={3}
        swatchUrl={topSwatchUrl} isActive={false}
        fillActive="none" fillInactive="none"
        strokeColor={hasTop ? STROKE : STROKE_LIGHT} strokeW={1.5} />

      {/* Backsplash area (subtle) */}
      <rect x="28" y="58" width="144" height="14" rx="1" fill="#f1f5f9" stroke={STROKE_LIGHT} strokeWidth="0.5" strokeDasharray="3 3" />

      {/* === COUNTERTOP (highlighted or colored) === */}
      <rect x="26" y="70" width="148" height="10" rx="2" fill={ctFill} stroke={ctStroke} strokeWidth={hasCt ? 1.5 : 2} />
      {/* Surface grain lines */}
      <line x1="36" y1="74" x2="60" y2="74" stroke={ctDetailColor} strokeWidth="0.5" opacity="0.4" />
      <line x1="70" y1="76" x2="100" y2="76" stroke={ctDetailColor} strokeWidth="0.5" opacity="0.4" />
      <line x1="110" y1="74" x2="140" y2="74" stroke={ctDetailColor} strokeWidth="0.5" opacity="0.4" />
      <line x1="50" y1="77" x2="80" y2="77" stroke={ctDetailColor} strokeWidth="0.5" opacity="0.3" />
      <line x1="120" y1="77" x2="160" y2="77" stroke={ctDetailColor} strokeWidth="0.5" opacity="0.3" />
      {/* Front edge / shadow */}
      <rect x="26" y="78" width="148" height="3" rx="1" fill={ctShadow} opacity="0.2" />
      {/* Gloss highlight on top surface */}
      {hasCt && (
        <rect x="30" y="70" width="140" height="4" rx="1" fill="rgba(255,255,255,0.2)" />
      )}

      {/* === BASE CABINETS === */}
      <SwatchRect id="ct-bl" x={30} y={84} w={42} h={40} rx={3}
        swatchUrl={bottomSwatchUrl} isActive={false}
        fillActive="none" fillInactive="none"
        strokeColor={hasBottom ? STROKE : STROKE_LIGHT} strokeW={1.5} />
      <SwatchRect id="ct-bc" x={78} y={84} w={44} h={40} rx={3}
        swatchUrl={bottomSwatchUrl} isActive={false}
        fillActive="none" fillInactive="none"
        strokeColor={hasBottom ? STROKE : STROKE_LIGHT} strokeW={1.5} />
      <SwatchRect id="ct-br" x={128} y={84} w={42} h={40} rx={3}
        swatchUrl={bottomSwatchUrl} isActive={false}
        fillActive="none" fillInactive="none"
        strokeColor={hasBottom ? STROKE : STROKE_LIGHT} strokeW={1.5} />

      {/* Floor line */}
      <line x1="20" y1="130" x2="180" y2="130" stroke={STROKE_LIGHT} strokeWidth="1" />
    </svg>
  )
}

/**
 * Handle (Pomolo)
 */
export const HandleSvg = ({ width = 200, height = 140 }) => (
  <svg width={width} height={height} viewBox="0 0 200 140" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="10" y="8" width="180" height="126" rx="4" fill={FILL_BG} stroke={STROKE_LIGHT} strokeWidth="1" />
    <rect x="30" y="18" width="140" height="106" rx="4" fill="#fff" stroke={STROKE} strokeWidth="1.5" />
    <rect x="38" y="26" width="124" height="90" rx="2" fill="none" stroke={STROKE_LIGHT} strokeWidth="1" />
    <rect x="70" y="64" width="60" height="12" rx="6" fill={ACCENT_LIGHT} stroke={ACCENT} strokeWidth="2" />
    <rect x="74" y="68" width="52" height="4" rx="2" fill={ACCENT} opacity="0.3" />
    <circle cx="78" cy="70" r="3" fill={ACCENT} opacity="0.6" />
    <circle cx="122" cy="70" r="3" fill={ACCENT} opacity="0.6" />
    <circle cx="100" cy="70" r="30" fill="none" stroke={ACCENT} strokeWidth="1" strokeDasharray="4 3" opacity="0.4" />
    <circle cx="50" cy="118" r="5" fill="none" stroke={STROKE} strokeWidth="1.5" />
    <circle cx="50" cy="118" r="2" fill={STROKE} />
    <line x1="85" y1="118" x2="115" y2="118" stroke={STROKE} strokeWidth="2.5" strokeLinecap="round" />
    <rect x="140" y="114" width="20" height="8" rx="4" fill="none" stroke={STROKE} strokeWidth="1.5" />
  </svg>
)

/**
 * Frame / Kasoma (GOLA vs OXI GOLA)
 */
export const FrameSvg = ({ width = 200, height = 140 }) => (
  <svg width={width} height={height} viewBox="0 0 200 140" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="10" y="8" width="180" height="126" rx="4" fill={FILL_BG} stroke={STROKE_LIGHT} strokeWidth="1" />
    <line x1="100" y1="16" x2="100" y2="126" stroke={STROKE_LIGHT} strokeWidth="1" strokeDasharray="4 4" />
    <rect x="24" y="30" width="62" height="72" rx="2" fill="#fff" stroke={ACCENT} strokeWidth="1.5" />
    <rect x="24" y="30" width="62" height="6" rx="1" fill={ACCENT_LIGHT} stroke={ACCENT} strokeWidth="1.5" />
    <rect x="28" y="32" width="54" height="2" rx="1" fill={ACCENT} opacity="0.5" />
    <rect x="32" y="44" width="46" height="50" rx="1" fill="none" stroke={STROKE_LIGHT} strokeWidth="0.5" />
    <text x="55" y="120" textAnchor="middle" fill={ACCENT} fontSize="10" fontWeight="700" fontFamily="sans-serif">GOLA</text>
    <rect x="114" y="30" width="62" height="72" rx="2" fill="#fff" stroke={STROKE} strokeWidth="1.5" />
    <rect x="118" y="34" width="54" height="64" rx="1" fill="none" stroke={STROKE_LIGHT} strokeWidth="1" />
    <line x1="134" y1="62" x2="150" y2="62" stroke={STROKE} strokeWidth="3" strokeLinecap="round" />
    <circle cx="137" cy="62" r="2" fill={STROKE} opacity="0.5" />
    <circle cx="147" cy="62" r="2" fill={STROKE} opacity="0.5" />
    <text x="145" y="120" textAnchor="middle" fill={STROKE} fontSize="9" fontWeight="600" fontFamily="sans-serif">{"OΧΙ GOLA"}</text>
  </svg>
)

/**
 * Full Kitchen Overview
 */
export const FullKitchenSvg = ({ width = 280, height = 180 }) => (
  <svg width={width} height={height} viewBox="0 0 280 180" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="10" y="5" width="260" height="170" rx="3" fill={FILL_BG} stroke={STROKE_LIGHT} strokeWidth="1" />
    <rect x="22" y="14" width="52" height="46" rx="3" fill={ACCENT_LIGHT} stroke={ACCENT} strokeWidth="1.5" />
    <line x1="48" y1="14" x2="48" y2="60" stroke={ACCENT} strokeWidth="1" strokeDasharray="2 2" />
    <circle cx="42" cy="37" r="1.5" fill={ACCENT} />
    <circle cx="54" cy="37" r="1.5" fill={ACCENT} />
    <rect x="80" y="14" width="62" height="46" rx="3" fill={ACCENT_LIGHT} stroke={ACCENT} strokeWidth="1.5" />
    <rect x="86" y="20" width="22" height="34" rx="1" fill="none" stroke={ACCENT} strokeWidth="0.75" opacity="0.5" />
    <rect x="112" y="20" width="22" height="34" rx="1" fill="none" stroke={ACCENT} strokeWidth="0.75" opacity="0.5" />
    <rect x="148" y="14" width="52" height="46" rx="3" fill={ACCENT_LIGHT} stroke={ACCENT} strokeWidth="1.5" />
    <line x1="174" y1="14" x2="174" y2="60" stroke={ACCENT} strokeWidth="1" strokeDasharray="2 2" />
    <circle cx="168" cy="37" r="1.5" fill={ACCENT} />
    <circle cx="180" cy="37" r="1.5" fill={ACCENT} />
    <rect x="208" y="14" width="42" height="138" rx="3" fill="#f1f5f9" stroke={STROKE} strokeWidth="1.5" />
    <line x1="212" y1="60" x2="246" y2="60" stroke={STROKE_LIGHT} strokeWidth="1" />
    <line x1="212" y1="96" x2="246" y2="96" stroke={STROKE_LIGHT} strokeWidth="1" />
    <line x1="224" y1="36" x2="234" y2="36" stroke={STROKE} strokeWidth="1.5" strokeLinecap="round" />
    <line x1="224" y1="78" x2="234" y2="78" stroke={STROKE} strokeWidth="1.5" strokeLinecap="round" />
    <line x1="224" y1="120" x2="234" y2="120" stroke={STROKE} strokeWidth="1.5" strokeLinecap="round" />
    <rect x="20" y="62" width="184" height="16" rx="1" fill="#f1f5f9" stroke={STROKE_LIGHT} strokeWidth="0.5" />
    <rect x="18" y="78" width="188" height="8" rx="2" fill="#e2e8f0" stroke={STROKE} strokeWidth="1" />
    <rect x="22" y="88" width="52" height="52" rx="3" fill="#fff" stroke={STROKE} strokeWidth="1.5" />
    <line x1="48" y1="88" x2="48" y2="140" stroke={STROKE_LIGHT} strokeWidth="1" strokeDasharray="2 2" />
    <circle cx="42" cy="112" r="1.5" fill={STROKE} />
    <circle cx="54" cy="112" r="1.5" fill={STROKE} />
    <rect x="80" y="88" width="62" height="52" rx="3" fill="#fff" stroke={STROKE} strokeWidth="1.5" />
    <rect x="90" y="80" width="42" height="6" rx="3" fill="none" stroke={STROKE} strokeWidth="1" />
    <line x1="111" y1="100" x2="111" y2="140" stroke={STROKE_LIGHT} strokeWidth="1" strokeDasharray="2 2" />
    <rect x="148" y="88" width="52" height="52" rx="3" fill="#fff" stroke={STROKE} strokeWidth="1.5" />
    <line x1="152" y1="103" x2="196" y2="103" stroke={STROKE_LIGHT} strokeWidth="1" />
    <line x1="152" y1="118" x2="196" y2="118" stroke={STROKE_LIGHT} strokeWidth="1" />
    <line x1="168" y1="96" x2="178" y2="96" stroke={STROKE} strokeWidth="1.5" strokeLinecap="round" />
    <line x1="168" y1="111" x2="178" y2="111" stroke={STROKE} strokeWidth="1.5" strokeLinecap="round" />
    <line x1="168" y1="128" x2="178" y2="128" stroke={STROKE} strokeWidth="1.5" strokeLinecap="round" />
    <rect x="22" y="142" width="184" height="6" rx="1" fill="#e2e8f0" stroke={STROKE_LIGHT} strokeWidth="0.5" />
    <rect x="208" y="154" width="42" height="3" rx="1" fill="#e2e8f0" stroke={STROKE_LIGHT} strokeWidth="0.5" />
    <line x1="10" y1="160" x2="270" y2="160" stroke={STROKE_LIGHT} strokeWidth="1" />
  </svg>
)

/**
 * Tall Cabinets (Olosoma / TALL section)
 * Side-view: tall/pantry cabinets highlighted in blue, wall + base in grey outline
 */
export const TallCabinetsSvg = ({ width = 200, height = 140, tallSwatchUrl, topSwatchUrl, bottomSwatchUrl, countertopColor }) => {
  const hasTall = Boolean(tallSwatchUrl)
  const hasTop = Boolean(topSwatchUrl)
  const hasBottom = Boolean(bottomSwatchUrl)

  return (
    <svg width={width} height={height} viewBox="0 0 200 140" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Back wall */}
      <rect x="20" y="8" width="160" height="126" rx="2" fill={FILL_BG} stroke={STROKE_LIGHT} strokeWidth="1" />

      {/* === WALL CABINETS (grey outline) === */}
      <SwatchRect id="tc-tl" x={30} y={16} w={36} h={36} rx={3}
        swatchUrl={topSwatchUrl} isActive={false}
        fillActive="none" fillInactive="none"
        strokeColor={hasTop ? STROKE : STROKE_LIGHT} strokeW={1.5} />
      <SwatchRect id="tc-tc" x={72} y={16} w={38} h={36} rx={3}
        swatchUrl={topSwatchUrl} isActive={false}
        fillActive="none" fillInactive="none"
        strokeColor={hasTop ? STROKE : STROKE_LIGHT} strokeW={1.5} />

      {/* === COUNTERTOP === */}
      <rect x="28" y="56" width="86" height="5" rx="1" fill={countertopColor || "#e2e8f0"} stroke={countertopColor ? "rgba(0,0,0,0.15)" : STROKE_LIGHT} strokeWidth="1" />

      {/* === BASE CABINETS (grey outline) === */}
      <SwatchRect id="tc-bl" x={30} y={63} w={36} h={40} rx={3}
        swatchUrl={bottomSwatchUrl} isActive={false}
        fillActive="none" fillInactive="none"
        strokeColor={hasBottom ? STROKE : STROKE_LIGHT} strokeW={1.5} />
      <SwatchRect id="tc-bc" x={72} y={63} w={38} h={40} rx={3}
        swatchUrl={bottomSwatchUrl} isActive={false}
        fillActive="none" fillInactive="none"
        strokeColor={hasBottom ? STROKE : STROKE_LIGHT} strokeW={1.5} />

      {/* === TALL CABINETS (highlighted / swatch-filled) === */}
      {/* Main tall cabinet — full height */}
      <SwatchRect id="tc-t1" x={120} y={16} w={24} h={110} rx={3}
        swatchUrl={tallSwatchUrl} isActive
        fillActive={ACCENT_LIGHT} fillInactive={ACCENT_LIGHT}
        strokeColor={ACCENT} strokeW={2} />
      {/* Second tall cabinet */}
      <SwatchRect id="tc-t2" x={148} y={16} w={24} h={110} rx={3}
        swatchUrl={tallSwatchUrl} isActive
        fillActive={ACCENT_LIGHT} fillInactive={ACCENT_LIGHT}
        strokeColor={ACCENT} strokeW={2} />

      {/* Tall cabinet details */}
      {!hasTall ? (
        <>
          {/* Left tall — upper + lower doors */}
          <line x1="120" y1="70" x2="144" y2="70" stroke={ACCENT} strokeWidth="1" />
          <circle cx="130" cy="44" r="1.5" fill={ACCENT} />
          <circle cx="130" cy="92" r="1.5" fill={ACCENT} />
          {/* Right tall — drawers */}
          <line x1="148" y1="44" x2="172" y2="44" stroke={ACCENT} strokeWidth="0.75" />
          <line x1="148" y1="70" x2="172" y2="70" stroke={ACCENT} strokeWidth="0.75" />
          <line x1="148" y1="96" x2="172" y2="96" stroke={ACCENT} strokeWidth="0.75" />
          <line x1="157" y1="30" x2="163" y2="30" stroke={ACCENT} strokeWidth="1.5" strokeLinecap="round" />
          <line x1="157" y1="57" x2="163" y2="57" stroke={ACCENT} strokeWidth="1.5" strokeLinecap="round" />
          <line x1="157" y1="83" x2="163" y2="83" stroke={ACCENT} strokeWidth="1.5" strokeLinecap="round" />
          <line x1="157" y1="110" x2="163" y2="110" stroke={ACCENT} strokeWidth="1.5" strokeLinecap="round" />
        </>
      ) : (
        <>
          <line x1="120" y1="70" x2="144" y2="70" stroke="rgba(255,255,255,0.5)" strokeWidth="1" />
          <circle cx="130" cy="44" r="1.5" fill="rgba(255,255,255,0.6)" />
          <circle cx="130" cy="92" r="1.5" fill="rgba(255,255,255,0.6)" />
          <line x1="148" y1="44" x2="172" y2="44" stroke="rgba(255,255,255,0.45)" strokeWidth="0.75" />
          <line x1="148" y1="70" x2="172" y2="70" stroke="rgba(255,255,255,0.45)" strokeWidth="0.75" />
          <line x1="148" y1="96" x2="172" y2="96" stroke="rgba(255,255,255,0.45)" strokeWidth="0.75" />
          <line x1="157" y1="30" x2="163" y2="30" stroke="rgba(255,255,255,0.6)" strokeWidth="1.5" strokeLinecap="round" />
          <line x1="157" y1="57" x2="163" y2="57" stroke="rgba(255,255,255,0.6)" strokeWidth="1.5" strokeLinecap="round" />
          <line x1="157" y1="83" x2="163" y2="83" stroke="rgba(255,255,255,0.6)" strokeWidth="1.5" strokeLinecap="round" />
          <line x1="157" y1="110" x2="163" y2="110" stroke="rgba(255,255,255,0.6)" strokeWidth="1.5" strokeLinecap="round" />
        </>
      )}

      {/* Floor line */}
      <line x1="20" y1="130" x2="180" y2="130" stroke={STROKE_LIGHT} strokeWidth="1" />
    </svg>
  )
}

/**
 * Compact section icons — small inline SVGs for section headers on the building page.
 * Each shows a stylized representation of just that cabinet type.
 */

// Wall section icon — 2 upper cabinets hanging from ceiling
export const WallSectionIcon = ({ size = 36, color = "#2563eb" }) => (
  <svg width={size} height={size} viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Ceiling line */}
    <line x1="2" y1="3" x2="34" y2="3" stroke={color} strokeWidth="1.5" opacity="0.3" />
    {/* Mounting lines */}
    <line x1="10" y1="3" x2="10" y2="7" stroke={color} strokeWidth="1" opacity="0.4" />
    <line x1="26" y1="3" x2="26" y2="7" stroke={color} strokeWidth="1" opacity="0.4" />
    {/* Left cabinet */}
    <rect x="3" y="7" width="14" height="18" rx="1.5" fill={`${color}15`} stroke={color} strokeWidth="1.5" />
    <line x1="10" y1="7" x2="10" y2="25" stroke={color} strokeWidth="0.75" strokeDasharray="1.5 1.5" />
    <circle cx="8" cy="16" r="1" fill={color} />
    <circle cx="12" cy="16" r="1" fill={color} />
    {/* Right cabinet */}
    <rect x="19" y="7" width="14" height="18" rx="1.5" fill={`${color}15`} stroke={color} strokeWidth="1.5" />
    <line x1="26" y1="7" x2="26" y2="25" stroke={color} strokeWidth="0.75" strokeDasharray="1.5 1.5" />
    <circle cx="24" cy="16" r="1" fill={color} />
    <circle cx="28" cy="16" r="1" fill={color} />
    {/* Wall hint */}
    <line x1="2" y1="32" x2="34" y2="32" stroke={color} strokeWidth="0.75" opacity="0.15" strokeDasharray="2 2" />
  </svg>
)

// Base section icon — 2 base cabinets on floor with countertop
export const BaseSectionIcon = ({ size = 36, color = "#16a34a" }) => (
  <svg width={size} height={size} viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Countertop */}
    <rect x="2" y="8" width="32" height="3" rx="1" fill={`${color}20`} stroke={color} strokeWidth="1" />
    {/* Left cabinet — door */}
    <rect x="3" y="12" width="14" height="18" rx="1.5" fill={`${color}15`} stroke={color} strokeWidth="1.5" />
    <line x1="10" y1="12" x2="10" y2="30" stroke={color} strokeWidth="0.75" strokeDasharray="1.5 1.5" />
    <circle cx="8" cy="21" r="1" fill={color} />
    <circle cx="12" cy="21" r="1" fill={color} />
    {/* Right cabinet — drawers */}
    <rect x="19" y="12" width="14" height="18" rx="1.5" fill={`${color}15`} stroke={color} strokeWidth="1.5" />
    <line x1="21" y1="18" x2="31" y2="18" stroke={color} strokeWidth="0.75" />
    <line x1="21" y1="24" x2="31" y2="24" stroke={color} strokeWidth="0.75" />
    <line x1="24" y1="15" x2="28" y2="15" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    <line x1="24" y1="21" x2="28" y2="21" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    <line x1="24" y1="27" x2="28" y2="27" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    {/* Floor */}
    <line x1="2" y1="33" x2="34" y2="33" stroke={color} strokeWidth="1" opacity="0.3" />
    {/* Legs */}
    <rect x="5" y="30" width="2" height="3" rx="0.5" fill={color} opacity="0.2" />
    <rect x="13" y="30" width="2" height="3" rx="0.5" fill={color} opacity="0.2" />
    <rect x="21" y="30" width="2" height="3" rx="0.5" fill={color} opacity="0.2" />
    <rect x="29" y="30" width="2" height="3" rx="0.5" fill={color} opacity="0.2" />
  </svg>
)

// Tall section icon — a tall/pantry column cabinet
export const TallSectionIcon = ({ size = 36, color = "#7c3aed" }) => (
  <svg width={size} height={size} viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Main tall cabinet */}
    <rect x="6" y="2" width="16" height="30" rx="1.5" fill={`${color}12`} stroke={color} strokeWidth="1.5" />
    {/* Upper door */}
    <line x1="8" y1="16" x2="20" y2="16" stroke={color} strokeWidth="0.75" />
    <circle cx="12" cy="9" r="1" fill={color} />
    <circle cx="12" cy="24" r="1" fill={color} />
    {/* Side tall cabinet (smaller, behind) */}
    <rect x="24" y="5" width="8" height="27" rx="1.5" fill={`${color}08`} stroke={color} strokeWidth="1" opacity="0.5" />
    <line x1="25" y1="18" x2="31" y2="18" stroke={color} strokeWidth="0.5" opacity="0.5" />
    <line x1="27" y1="12" x2="29" y2="12" stroke={color} strokeWidth="1" strokeLinecap="round" opacity="0.5" />
    <line x1="27" y1="24" x2="29" y2="24" stroke={color} strokeWidth="1" strokeLinecap="round" opacity="0.5" />
    {/* Floor */}
    <line x1="4" y1="34" x2="34" y2="34" stroke={color} strokeWidth="0.75" opacity="0.3" />
  </svg>
)

export default {
  WallCabinetsSvg,
  BaseCabinetsSvg,
  TallCabinetsSvg,
  CountertopSvg,
  HandleSvg,
  FrameSvg,
  FullKitchenSvg,
  WallSectionIcon,
  BaseSectionIcon,
  TallSectionIcon,
}
